import React, { useEffect, useState } from 'react';
import { Box, Container, Grid, Paper, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { RootState } from '../../../store/store';
import { setBookingDetails } from '../../../store/slices/bookingCreationSlice';
import { formatAtStation, stationPickerDay, zoneOfLocation } from '../../../utils/stationTime';
import { useTranslation } from 'react-i18next';
import { useLazyGetBookingDetailsQuery } from '../../../api/booking/bookingApiSlice';
import { useNavigate, useParams } from 'react-router';
import StatusBadge from '../../../components/StatusBadge';
import CustomButton from '../../../components/CustomButton';
import CustomSeparator from '../../../components/CustomBreadCrumb';
import { paths } from '../../../routes';
import { setConfirmationDialogContent, setIsConfirmationDialog } from '../../../store/slices/globalSlice';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { useCancelBooking } from '../../../flows/cancelBookingFlow';
import { useDeleteBooking } from '../../../flows/deleteBookingFlow';
import { deleteBookingErrorMessage } from '../../../utils/deleteBookingErrorMessage';
import SnackbarComponent from '../../common/SnackbarComponent';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import { useIsMaster } from '../../../common/useIsMaster';
import Permission from '../../../common/permissions';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import { formatDateTime } from '../../../utils';
import i18n from 'i18next';

const BookingDetailsForBlocker = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const params = useParams<{ bookingId: string }>();
    const orderId = params.bookingId ?? '';
    const [dialogAction, setDialogAction] = useState<'cancel' | 'delete' | null>(null);
    const [snackbarData, setSnackbarData] = useState({
        open: false,
        message: '',
        severity: 'info' as 'success' | 'info' | 'warning' | 'error',
    });

    const [trigger, { data }] = useLazyGetBookingDetailsQuery();
    const cancelBooking = useCancelBooking();
    const deleteBooking = useDeleteBooking();

    const hasEditBookingsPermission = useSelector(
        makeHasPermissionSelector(Permission.editBookings()),
    );
    // Deleting a booking is a master-only action — hidden from tenant owners
    // even though they hold the deleteBookings permission.
    const isMaster = useIsMaster();

    const bookingDetails = useSelector((state: RootState) => state.bookingCreation.bookingDetails);
    // #3219: the blocked days are the location's.
    const timeZone = zoneOfLocation(bookingDetails?.locationTimeZone);

    const breadcrumbItems = [
        { label: t('booking_details.booking'), href: paths.rentalBooking },
        { label: data?.bookingNumber ?? '', isActive: true },
    ];

    useEffect(() => {
        if (orderId) {
            trigger({ orderId });
        }
    }, [orderId]);

    useEffect(() => {
        if (data) {
            dispatch(setBookingDetails(data));
        }
    }, [data, dispatch]);

    const handleClickCancelBooking = () => {
        setDialogAction('cancel');
        dispatch(setIsConfirmationDialog(true));
        dispatch(
            setConfirmationDialogContent({
                // A blocker order has no customer and no contact row, so the
                // backend can never mail one — asking "inform the customer?"
                // here would be a question with no possible answer.
                content: t('booking_details.confirmDialog.content_blocker'),
                headerText: t('booking_details.confirmDialog.headerText'),
                leftButtonText: t('booking_details.confirmDialog.leftButtonText'),
                // NOT the shared `rightButtonText`: that now reads "Stornieren
                // und E-Mail senden", and a blocker has no customer and no
                // contact row, so the cancel endpoint can never mail one. A
                // button promising a mail nobody will receive is worse than a
                // plain one.
                rightButtonText: t('booking_details.confirmDialog.rightButtonText_blocker'),
            }),
        );
    };

    const handleClickDeleteBooking = () => {
        setDialogAction('delete');
        dispatch(setIsConfirmationDialog(true));
        dispatch(
            setConfirmationDialogContent({
                content: t('booking_details.confirmDeleteDialog.content'),
                headerText: t('booking_details.confirmDeleteDialog.headerText'),
                leftButtonText: t('booking_details.confirmDeleteDialog.leftButtonText'),
                rightButtonText: t('booking_details.confirmDeleteDialog.rightButtonText'),
            }),
        );
    };

    async function handleCancelBooking() {
        try {
            // Explicitly `save_only` rather than relying on the backend's
            // blocker skip: the screen states what it wants, the endpoint
            // enforces what is possible.
            const response = await cancelBooking('save_only');
            if (response) {
                setSnackbarData({
                    open: true,
                    message: t('booking_details.cancel_booking.success_message'),
                    severity: 'success',
                });
                setTimeout(() => {
                    navigate(paths.rentalBooking);
                }, 1500);
            }
        } catch (error) {
            setSnackbarData({
                open: true,
                message: t('booking_details.cancel_booking.error_message'),
                severity: 'error',
            });
        }
    }

    async function handleDeleteBooking() {
        try {
            const response = await deleteBooking();
            if (response) {
                setSnackbarData({
                    open: true,
                    message: t('booking_details.delete_booking.success_message'),
                    severity: 'success',
                });
                setTimeout(() => {
                    navigate(paths.rentalBooking);
                }, 1500);
            }
        } catch (error) {
            setSnackbarData({
                open: true,
                message: deleteBookingErrorMessage(error, t),
                severity: 'error',
            });
        }
    }

    const handleCloseSnackbar = () => {
        setSnackbarData((prev) => ({ ...prev, open: false }));
    };

    const handleConfirmAction = () => {
        if (dialogAction === 'cancel') {
            handleCancelBooking();
        } else if (dialogAction === 'delete') {
            handleDeleteBooking();
        }
    };

    const nightCount = bookingDetails?.checkIn && bookingDetails?.checkOut
        ? stationPickerDay(bookingDetails.checkOut, timeZone).diff(stationPickerDay(bookingDetails.checkIn, timeZone), 'day')
        : 0;

    return (
        <Container maxWidth={false} className={'px-0 sm:px-4'}>
            <Box sx={{ py: 3 }}>
                <PageHeader>
                    <PageHeadline>
                        {t('booking_details.title')}
                    </PageHeadline>
                </PageHeader>
                <div className={'mb-6'}>
                    <CustomSeparator items={breadcrumbItems} />
                </div>

                <Paper sx={{ padding: '24px' }}>
                    {/* Header */}
                    <div className={'flex justify-between mb-6 bg-[#F5F5F5] p-4 rounded-lg'}>
                        <div className={'flex flex-col w-full'}>
                            <div className={'flex flex-col xl:flex-row justify-between'}>
                                <div className={'flex items-center mb-2'}>
                                    <h1 className={'text-2xl font-medium mr-4'}>{bookingDetails?.bookingNumber}</h1>
                                    <StatusBadge type={bookingDetails?.status ?? 'blocked'} />
                                </div>
                                <div className={'flex flex-wrap gap-2 mb-2'}>
                                    {isMaster && (
                                        <CustomButton
                                            title={t('booking_details.buttons.delete_booking')}
                                            onClick={handleClickDeleteBooking}
                                            isFilled={false}
                                            variant={'delete'}
                                        />
                                    )}
                                    {hasEditBookingsPermission && bookingDetails?.status !== 'canceled' && (
                                        <CustomButton
                                            title={t('booking_details.buttons.cancel_booking')}
                                            onClick={handleClickCancelBooking}
                                            isFilled={false}
                                        />
                                    )}
                                </div>
                            </div>
                            <p className={'text-sm text-gray-500'}>
                                {t('booking_details.created_on')}:{' '}
                                <span className={'font-bold'}>
                                    {bookingDetails?.created_at
                                        ? formatDateTime(bookingDetails.created_at, i18n.language as 'en' | 'de', { timeZone })
                                        : t('common.not_applicable')}
                                </span>
                            </p>
                        </div>
                    </div>

                    {/* Blocker Details */}
                    <Typography variant={'h6'} fontWeight={600} sx={{ mb: 3 }}>
                        {t('create_blocker.summary.title')}
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant={'body2'} color={'text.secondary'} sx={{ mb: 0.5 }}>
                                {t('create_blocker.summary.location')}
                            </Typography>
                            <Typography variant={'body1'} fontWeight={500}>
                                {bookingDetails?.city || t('common.not_applicable')}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant={'body2'} color={'text.secondary'} sx={{ mb: 0.5 }}>
                                {t('create_blocker.summary.apartment')}
                            </Typography>
                            <Typography variant={'body1'} fontWeight={500}>
                                {bookingDetails?.apartment?.name || t('common.not_applicable')}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant={'body2'} color={'text.secondary'} sx={{ mb: 0.5 }}>
                                {t('create_blocker.start_date')}
                            </Typography>
                            <Typography variant={'body1'} fontWeight={500}>
                                {bookingDetails?.checkIn
                                    ? formatAtStation(bookingDetails.checkIn.toDate(), 'dd LLL yyyy', 'en', timeZone)
                                    : t('common.not_applicable')}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant={'body2'} color={'text.secondary'} sx={{ mb: 0.5 }}>
                                {t('create_blocker.end_date')}
                            </Typography>
                            <Typography variant={'body1'} fontWeight={500}>
                                {bookingDetails?.checkOut
                                    ? formatAtStation(bookingDetails.checkOut.toDate(), 'dd LLL yyyy', 'en', timeZone)
                                    : t('common.not_applicable')}
                            </Typography>
                        </Grid>

                        {nightCount > 0 && (
                            <Grid item xs={12} sm={6}>
                                <Typography variant={'body2'} color={'text.secondary'} sx={{ mb: 0.5 }}>
                                    {t('create_blocker.summary.period')}
                                </Typography>
                                <Typography variant={'body1'} fontWeight={500}>
                                    {t('create_blocker.summary.nights', { count: nightCount })}
                                </Typography>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                            <Typography variant={'body2'} color={'text.secondary'} sx={{ mb: 0.5 }}>
                                {t('create_blocker.reason')}
                            </Typography>
                            <Typography variant={'body1'} fontWeight={500}>
                                {bookingDetails?.reasonForBlocking
                                    ? t(`issue_type.${bookingDetails.reasonForBlocking}`)
                                    : t('common.not_applicable')}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant={'body2'} color={'text.secondary'} sx={{ mb: 0.5 }}>
                                {t('create_blocker.comment')}
                            </Typography>
                            <Typography variant={'body1'} fontWeight={500} sx={{ whiteSpace: 'pre-wrap' }}>
                                {bookingDetails?.comment || t('common.not_applicable')}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            <ConfirmationDialog handleClickConfirm={handleConfirmAction} />

            <SnackbarComponent
                open={snackbarData.open}
                message={snackbarData.message}
                severity={snackbarData.severity}
                onClose={handleCloseSnackbar}
            />
        </Container>
    );
};

export default BookingDetailsForBlocker;
